import { Field, useFormikContext } from "formik";
import React, { useState } from "react";
import { components } from "react-select";
import AsyncCreatableSelect from "react-select/async-creatable";
import { useNavigate } from "react-router-dom";
import DownArrowIcon from "../../assets/icons/down-arrow-icon";
import StarIcon from "../../assets/icons/req-star";
import ErrorMsg from "./error-msg";
import "./form.css";

const SearchCustomerField = ({
  isRequired = true,
  name,
  cusType = "Customer",
  handleCreateOption,
  loadOptions,
  type = "text", // Changed default to "text" for better clarity
  isLabelReq = true,
  onChange = () => { },
  placeholder = "select and add...",
  Icon = <DownArrowIcon />,
  label = "Customer Name/Phone",
  disabled = false,
  autoFocus = false,
  labelClass = "flex gap-1 whitespace-nowrap text-[var(--search-input-label-color)]  text-[length:--search-label-font-size] font-[var(--search-label-font-weight)]",
  minWidth = "",
  isValidNewOption = false,
  className = "",
  maxlength = 100,
  menuPosition = "",
  menuPortalTarget = null,
}) => {
  const { values: formValues } = useFormikContext();

  const [inputValue, setInputValue] = useState("");

  const DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        {Icon}
      </components.DropdownIndicator>
    );
  };

  const navigate = useNavigate();

  const NoOptionsMessage = ({ inputValue, ...props }) => (
    <button
      onClick={() => navigate("/admin/add-customer")}
      className="py-1 cursor-pointer"
    >
      <span className="text-primary text-sm px-3">
        <span className="fs_18 pr-1">+</span> New {cusType}
      </span>
    </button>
  );

  // Universal input handler - allows both text and numbers
  const handleUniversalInput = (e) => {
    // Allow Ctrl+V (or Cmd+V on Mac) for pasting
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "v") {
      return; // Allow pasting
    }

    // Allow all navigation and control keys
    if (
      [8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+X, Ctrl+Z
      (e.keyCode === 65 && (e.ctrlKey || e.metaKey)) ||
      (e.keyCode === 67 && (e.ctrlKey || e.metaKey)) ||
      (e.keyCode === 88 && (e.ctrlKey || e.metaKey)) ||
      (e.keyCode === 90 && (e.ctrlKey || e.metaKey)) ||
      // Allow home, end, left, right, down, up
      (e.keyCode >= 35 && e.keyCode <= 40)
    ) {
      return;
    }

    // Allow all alphanumeric characters, spaces, and special characters
    // No restrictions - user can type anything
  };

  const handleInputChange = (newValue, { action }) => {
    if (action === "input-change" && newValue.length <= maxlength) {
      // Allow all input - no restrictions based on type
      // The field can handle both text and numbers dynamically
      setInputValue(newValue);
    }
  };

  const handleChange = (selectedOption, actionMeta) => {
    if (actionMeta?.action === "clear" || actionMeta?.action === "select-option") {
      setInputValue("");
    }
    onChange(selectedOption, actionMeta);
  };

  // Detect content type and validate new option accordingly
  const validateNewOption = (inputValue) => {
    if (!isValidNewOption) return false;

    if (!inputValue || inputValue.trim().length === 0) return false;

    // Check if input contains alphabets - if so, don't allow creating new option
    const containsAlphabets = /[a-zA-Z]/.test(inputValue);
    if (containsAlphabets) {
      return false;
    }

    // Only allow numeric inputs for creating new options
    const isNumeric = /^[-]?\d*\.?\d+$/.test(inputValue.trim());
    return isNumeric;
  };

  return (
    <Field name={name}>
      {({ field }) => {
        return (
          <>
            <div className="flex flex-col">
              {isLabelReq && (
                <label
                  style={{
                    minWidth: minWidth,
                    minHeight: "1.2rem",
                  }}
                  className={`${labelClass} `}
                >
                  {label}
                  {isRequired && <StarIcon />}
                </label>
              )}
              <AsyncCreatableSelect
                inputValue={inputValue}
                onInputChange={handleInputChange}
                menuPlacement="bottom"
                isValidNewOption={(inputValue) => validateNewOption(inputValue)}
                autoFocus={autoFocus}
                onKeyDown={handleUniversalInput}
                name={name}
                isClearable
                value={field.value}
                components={{
                  ...components,
                  DropdownIndicator,
                  NoOptionsMessage,
                }}
                placeholder={placeholder}
                menuPosition="fixed"
                menuPortalTarget={menuPortalTarget}
                className={`w-full z-10 border-radius ${className}`}
                styles={{
                  ...getCustomStyles(type),
                  menuPortal: base => ({ ...base, zIndex: 99999 })
                }}
                {...field}
                onCreateOption={handleCreateOption}
                onChange={handleChange}
                loadOptions={loadOptions}
                isDisabled={disabled}
              />
            </div>
            {/* Uncomment if you want to show error messages */}
            {/* <div
            style={{
              height: "1rem",
            }}
            className={`text-right w-full fs_12 ${errorClass}`}
          >
            {isRequired && <ErrorMsg name={name} />}
          </div> */}
          </>
        );
      }}
    </Field>
  );
};

export default SearchCustomerField;

// Custom styles function that adapts to content
const getCustomStyles = (type) => ({
  indicatorSeparator: () => ({ display: "none" }),
  control: (base, state) => ({
    ...base,
    backgroundColor: "var(--search-input-bg)",
    color: "var(--search-input-color)",
    fontSize: "var(--search-input-font-size)",
    fontWeight: "var(--search-input-font-weight)",
    borderColor: state.isFocused
      ? "var(--search-input-focus)"
      : "var(--search-input-border)",
    boxShadow: "none",
    borderRadius: "6px",
    padding: "0",
    margin: "0",
    minHeight: "38px",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "3px 6px",
  }),
  option: (provided, state) => ({
    ...provided,
    color: state.data.color ? state.data.color : "var(--search-input-color)",
    backgroundColor: state.isSelected
      ? "#e5ecffb2"
      : state.isFocused
        ? "#0000002E"
        : "transparent",
    borderBottom: "1px solid #F5F5F5",
    fontFamily: "Inter, sans-serif",
    fontSize: "var(--search-place-font-size)",
    fontWeight: "var(--search-place-font-weight)",
    "&:hover": {
      backgroundColor: "#0000002E",
    },
  }),
  placeholder: (defaultStyles) => ({
    ...defaultStyles,
    color: "var(--search-place-color)",
    fontFamily: "Inter, sans-serif",
    fontSize: "var(--search-place-font-size)",
    fontWeight: "var(--search-place-font-weight)",
    opacity: "0.9",
  }),
  singleValue: (base) => ({
    ...base,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "var(--search-input-color)",
    fontSize: "var(--search-input-font-size)",
    fontWeight: "var(--search-input-font-weight)",
    // Dynamic text alignment - no longer dependent on type prop
    textAlign: "left", // Always left-aligned since content can be mixed
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#E5ECFF",
    gap: "5px",
    padding: "0px 6px",
  }),
  dropdownIndicator: () => ({ display: "none" }),
  menu: (base) => ({
    ...base,
    backgroundColor: "white",
    zIndex: 9999,
    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    border: "1px solid var(--search-input-border)",
  }),
});
